import { AuthProviderProps } from 'react-oidc-context';
import { InMemoryWebStorage, OidcMetadata, User, WebStorageStateStore } from 'oidc-client-ts';
import { backRolesToOauthFormat, CART_ROLE_PREFIX, env, ENV, PROD_ALICE, TEST_ALICE } from './constants';

const ssoScopes = 'openid authorization_group entitlement_group job_title scoped_entitlement organizational_data profile offline_access';
let cartt = '';

export const getCaRTT = () => {
  return cartt;
};

export const setCaRTT = (t: string) => {
  cartt = t;
};

const ssoClientRoot = `${window.location.origin}/front`;
const registerUrl = env === ENV.PRO ? PROD_ALICE : TEST_ALICE;

// Para el SSO solo hay entornos de INT y PRO.
const ssoAuthority = {
  local: 'https://localhost',
  development: 'https://sso-int.mercedes-benz.com',
  int: 'https://sso-int.mercedes-benz.com',
  production: 'https://sso.mercedes-benz.com',
};

const ssoClientID = {
  local: '52533533-34ea-4f9e-90a0-950c1375ca3e',
  development: '52533533-34ea-4f9e-90a0-950c1375ca3e',
  int: '0fa15af4-efcf-4c65-badd-a43ebea8c8c9',
  production: 'edb891ef-6735-4b91-aaaf-6b18120b016e',
};

const permissions = {
  read: 'CART.Read',
  write: 'CART.Write',
};

const mockUser = {
  USER: 'USER',
  ADMIN: 'ADMIN',
};

const mockUserRights = {
  USER: [
    'CART.Read',
  ],
  ADMIN: [
    'CART.Read',
    'CART.Write',
  ],
};

/**
 * Para entropía mayor que 128 bits, necesitamos length de 22 para un conjunto de 64 caracteres
 * +info sobre entropía https://inversegravity.net/2019/password-entropy/
 */
const randomString = (length: number = 22) => {
  const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._';
  let result = '';

  while (length > 0) {
    const bytes = new Uint8Array(16);
    // @ts-expect-error:next-line
    const crypto = window.crypto || window.msCrypto;
    const random = crypto.getRandomValues(bytes);

    /* eslint-disable no-param-reassign, no-plusplus */
    random.forEach((c) => {
      if (length === 0) {
        return;
      }

      if (c < charset.length) {
        result += charset[c];
        length--;
      }
    });
  }

  return result;
};

const oauthRoleToBackRole = (role: string) => role.replace(CART_ROLE_PREFIX, '').toUpperCase();
const backRoleToOauthRole = (role: string) => `${CART_ROLE_PREFIX}${backRolesToOauthFormat[role] || ''}`;
const BACK_ROLE_PERSISTENCE_EQUIVALENCE: Record<string, string> = {
  CAMPAIGN_MANAGER_JV: 'CAMPAIGN_MANAGER',
  QMB_PLANT_APPROVER_JV: 'QMB_PLANT_APPROVER',
};
const normalizeBackRoleForPersistence = (role: string = ''): string => BACK_ROLE_PERSISTENCE_EQUIVALENCE[role] || role;
const BACK_ROLE_PREFIX = 'CART_';
const stripCartPrefix = (role: string = ''): string => (
  role.startsWith(BACK_ROLE_PREFIX) ? role.slice(BACK_ROLE_PREFIX.length) : role
);

const normalizeBackRole = (role?: string): string => {
  if (!role) return '';
  if (role.startsWith(CART_ROLE_PREFIX)) return oauthRoleToBackRole(role);

  return stripCartPrefix(role).toUpperCase();
};

const mapBackRolesToOauth = (roles: string[] = []): string[] => roles
  .map((role) => backRoleToOauthRole(stripCartPrefix(role || '')))
  .filter((role) => role && role !== CART_ROLE_PREFIX);

const resolveSelectedOauthRole = (
  settingsRole?: string,
  backRoleSelected?: string,
  backRolesList: string[] = [],
): string => {
  const oauthRoles = mapBackRolesToOauth(backRolesList);
  const roleCandidates = [settingsRole, backRoleSelected]
    .map((role) => backRoleToOauthRole(normalizeBackRole(role)))
    .filter(Boolean);
  const firstValidCandidate = roleCandidates.find((role) => oauthRoles.includes(role));

  if (firstValidCandidate) return firstValidCandidate;

  return oauthRoles[0] || '';
};

const resolveSelectedBackRole = (
  settingsRole?: string,
  backRoleSelected?: string,
  backRolesList: string[] = [],
): string => {
  const resolvedOauthRole = resolveSelectedOauthRole(settingsRole, backRoleSelected, backRolesList);

  return resolvedOauthRole ? oauthRoleToBackRole(resolvedOauthRole) : '';
};

const nonce = randomString();

export const isLocal = env === ENV.LOCAL;
export const https = 'https://localhost:3000';

const metadata: Partial<OidcMetadata> | undefined = isLocal
  ? {
    issuer: 'https://sso-int.mercedes-benz.com',
    authorization_endpoint: 'https://sso-int.mercedes-benz.com/as/authorization.oauth2',
    end_session_endpoint: 'https://sso-int.mercedes-benz.com/idp/startSLO.ping',

    // We have to use a proxy-server for those enpodints in other to avoid CORS Issues
    token_endpoint: `${https}/as/token.oauth2`,
    revocation_endpoint: `${https}/as/revoke_token.oauth2`,
    userinfo_endpoint: `${https}/idp/userinfo.openid`,
  }
  : undefined;

export const oidcConfig: AuthProviderProps = {
  authority: `${ssoAuthority[env]}`,
  client_id: ssoClientID[env],
  redirect_uri: isLocal ? 'https://localhost:3000/' : window.location.origin,
  silent_redirect_uri: '', // Disable iframe-based silent renew to avoid CSP frame-ancestors violation
  metadata,
  automaticSilentRenew: false, // Handled manually in AuthContext via handleTokenExpiring
  silentRequestTimeoutInSeconds: 60,
  accessTokenExpiringNotificationTimeInSeconds: 300, // Notify 5 minutes before expiration
  metadataUrl: `${ssoAuthority[env]}/.well-known/openid-configuration`,
  response_type: 'code',
  userStore: new WebStorageStateStore({ store: new InMemoryWebStorage() }),
  loadUserInfo: true,
  scope: ssoScopes,
  onSigninCallback: (user: User | void) => {
    window.history.replaceState({}, document.title, window.location.pathname);
    setCaRTT(user?.access_token || getCaRTT());
  },
  extraQueryParams: {
    acr_values: 'gas:strong',
    nonce,
    // state,
  },
};

export default {
  env,
  randomString,
  registerUrl,
  ssoScopes,
  ssoClientRoot,
  ssoAuthority: ssoAuthority[env],
  ssoClientID: ssoClientID[env],
  permissions,
  mockUser,
  mockUserRights,
  oauthRoleToBackRole,
  backRoleToOauthRole,
  stripCartPrefix,
  normalizeBackRole,
  normalizeBackRoleForPersistence,
  mapBackRolesToOauth,
  resolveSelectedOauthRole,
  resolveSelectedBackRole,
  getCaRTT,
  setCaRTT,
};
